import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config';
import { handlePhotoButtonClick } from '../utils/photoHandler';
import { handleSocioUpdate } from '../utils/socioUpdateHandler';
import { construirUrlFoto } from '../utils/photoUrlHelper';
import './EditarAdmin.css';
import LogoutButton from '../components/LogoutButton';

function EditarAdmin() {
  const navigate = useNavigate();
  const usuarioLS = JSON.parse(localStorage.getItem('usuario'));
  const [usuario, setUsuario] = useState(usuarioLS);
  const [form, setForm] = useState({
    nombreCompleto: usuarioLS.nombreCompleto || '',
    documento: usuarioLS.documento || '',
    tipoDocumento: usuarioLS.tipoDocumento || '',
    direccion: usuarioLS.direccion || '',
    correo: usuarioLS.correo || '',
    telefono: usuarioLS.telefono ? usuarioLS.telefono.replace(/^\+\d+\s/, '') : '',
    fechaNacimiento: usuarioLS.fechaNacimiento ? usuarioLS.fechaNacimiento.substring(0, 10) : '',
    administrador: usuarioLS.administrador ? 'Sí' : 'No',
    novedadesMail: usuarioLS.novedadesMail !== false,
  });
  const [codigoPais, setCodigoPais] = useState(usuarioLS.telefono && usuarioLS.telefono.match(/^\+\d+/) ? usuarioLS.telefono.match(/^\+\d+/)[0] : '+598');
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const fileInputRef = useRef();
  const [fotoKey, setFotoKey] = useState(Date.now());
  const [fotoPreview, setFotoPreview] = useState(usuarioLS.foto ? construirUrlFoto(usuarioLS.foto) : '');
  const [nuevaFoto, setNuevaFoto] = useState(null);
  const [fotoAnterior, setFotoAnterior] = useState(usuarioLS.foto || null);
  const [showModal, setShowModal] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Función para verificar si el token ha expirado
  const checkTokenExpiration = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      navigate('/login');
      return false;
    }
    
    try {
      // Decodificar el token JWT para verificar la expiración
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      if (payload.exp && payload.exp < currentTime) {
        // Token expirado
        localStorage.removeItem('token');
        localStorage.removeItem('usuario');
        navigate('/login');
        return false;
      }
      return true;
    } catch (error) {
      // Token inválido
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      navigate('/login');
      return false;
    }
  };

  // Función utilitaria para manejar errores de autenticación
  const handleAuthError = (response, errorMessage = 'Error de autenticación') => {
    if (response.status === 401 || response.status === 403) {
      setError('Sesión expirada. Serás redirigido al login.');
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('usuario');
        navigate('/login');
      }, 2000);
      return true;
    }
    return false;
  };

  // Verificar token al montar el componente
  useEffect(() => {
    checkTokenExpiration();
  }, []);

  useEffect(() => {
    // Aquí podrías cargar datos actualizados del admin si lo deseas
  }, []);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCodigoPaisChange = e => {
    setCodigoPais(e.target.value);
  };

  const handleTelefonoChange = e => {
    setForm({ ...form, telefono: e.target.value });
  };

  const handleFotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNuevaFoto(file);
      setFotoPreview(URL.createObjectURL(file));
      setFotoKey(Date.now());
      
      // Resetear el input para permitir seleccionar el mismo archivo de nuevo
      e.target.value = '';
    }
  };

  const hasUnsavedChanges = () => {
    const cambiosFormulario = Object.keys(form).some(key => {
      let formVal = form[key];
      let userVal = usuario[key];
      
      // Convertir null/undefined a string vacío para comparación
      formVal = formVal ?? '';
      userVal = userVal ?? '';
      
      // Si es fecha, comparar solo yyyy-mm-dd
      if (key === 'fechaNacimiento' && formVal && userVal) {
        formVal = formVal.substring(0, 10);
        userVal = userVal.substring(0, 10);
      }
      
      // Para el campo teléfono, comparar sin el código de país
      if (key === 'telefono') {
        // Remover código de país del valor guardado para comparar
        const userValSinCodigo = userVal.replace(/^\+\d+\s/, '');
        return formVal !== userValSinCodigo;
      }
      
      // Para el campo administrador, comparar valores booleanos
      if (key === 'administrador') {
        const formBool = formVal === 'Sí';
        const userBool = userVal === true || userVal === 'Sí';
        return formBool !== userBool;
      }
      
      // Para el campo novedadesMail, comparar valores booleanos
      if (key === 'novedadesMail') {
        const formBool = formVal === true || formVal === 'true';
        const userBool = userVal === true || userVal === 'true';
        return formBool !== userBool;
      }
      
      return formVal !== userVal;
    });

    // Verificar si hay cambios de foto pendientes
    const cambiosFoto = fotoAnterior && usuario.foto !== fotoAnterior;

    return cambiosFormulario || cambiosFoto;
  };

  const handleLogout = (e) => {
    if (hasUnsavedChanges()) {
      e.preventDefault();
      setShowModal(true);
      setLogoutPending(true);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      navigate('/login');
    }
  };

  const handleSaveAndLogout = async () => {
    await handleSubmit(new Event('submit'));
    setShowModal(false);
    setLogoutPending(false);
    setTimeout(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      navigate('/login');
    }, 500);
  };

  const handleLogoutWithoutSave = async () => {
    setShowModal(false);
    setLogoutPending(false);
    
    // Si hay una foto nueva que no se guardó, revertirla
    if (fotoAnterior && usuario.foto !== fotoAnterior) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_ENDPOINTS.SOCIOS}/${usuario.id}/revertir-foto`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
          },
          body: JSON.stringify({ fotoAnterior })
        });
        
        if (!res.ok) {
          console.error('Error al revertir foto:', await res.text());
        }
      } catch (err) {
        console.error('Error de red al revertir foto:', err);
      }
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    navigate('/login');
  };

  const handleCancelModal = () => {
    setShowModal(false);
    setLogoutPending(false);
  };

  const handleChangePassword = () => {
    setShowPasswordModal(true);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSavePassword = async () => {
    if (!newPassword.trim()) {
      setError('La contraseña es obligatoria');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    setError('');
    setMensaje('');
    try {
      const token = localStorage.getItem('token');
              const res = await fetch(API_ENDPOINTS.CAMBIAR_CONTRASENA, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ 
          nuevaContrasena: newPassword 
        })
      });
      const data = await res.json();
      if (res.ok) {
        setMensaje('Contraseña actualizada correctamente');
        setShowPasswordModal(false);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.message || 'Error al cambiar contraseña');
      }
    } catch (err) {
      setError('Error de red');
    }
    setLoading(false);
  };

  const handleCancelPassword = () => {
    setShowPasswordModal(false);
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  const validar = () => {
    if (!form.nombreCompleto?.trim()) return 'El nombre completo es obligatorio.';
    if (!form.documento?.trim()) return 'El documento es obligatorio.';
    if (!form.tipoDocumento) return 'El tipo de documento es obligatorio.';
    if (form.correo && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.correo)) return 'El correo no es válido.';
    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    await handleSocioUpdate({
      form,
      socio: usuario,
      codigoPais,
      fotoAnterior,
      nuevaFoto,
      setMensaje,
      setError,
      setLoading,
      setSocio: (updatedSocio) => {
        setUsuario(updatedSocio);
        localStorage.setItem('usuario', JSON.stringify(updatedSocio));
        setNuevaFoto(null);
      },
      setForm: (updatedSocio) => {
        // handleSocioUpdate llama setForm con el socio actualizado
        // Necesitamos transformarlo al formato del form de admin
        setForm({
          nombreCompleto: updatedSocio.nombreCompleto || '',
          documento: updatedSocio.documento || '',
          tipoDocumento: updatedSocio.tipoDocumento || '',
          direccion: updatedSocio.direccion || '',
          correo: updatedSocio.correo || '',
          telefono: updatedSocio.telefono ? updatedSocio.telefono.replace(/^\+\d+\s/, '') : '',
          fechaNacimiento: updatedSocio.fechaNacimiento ? updatedSocio.fechaNacimiento.substring(0, 10) : '',
          administrador: updatedSocio.administrador ? 'Sí' : 'No',
          novedadesMail: updatedSocio.novedadesMail !== false,
        });
      },
      setFotoPreview,
      setFotoAnterior,
      setFotoKey,
      socioId: usuario.id,
      validar
    });
  };

  return (
    <div className="panel-socio-bg">
      <div className="panel-socio-card">
        <h2 className="panel-socio-title">🛠️ Editar Administrador</h2>
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '1rem', width: '100%', justifyContent: 'center'}}>
          {fotoPreview && <img key={fotoKey} src={fotoPreview} alt="Foto del admin" className="panel-socio-foto" />}
          <button
            type="button"
            className="btn-panel-socio btn-cambiar-foto"
            style={{marginLeft: 16, padding: '0.4rem 1rem', fontSize: '0.95rem', minWidth: 0, width: 'auto', height: 38}}
            onClick={() => handlePhotoButtonClick(fileInputRef, subiendoFoto)}
            disabled={subiendoFoto}
          >
            {subiendoFoto ? 'Subiendo...' : 'Cambiar foto'}
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{display: 'none'}}
            onChange={handleFotoChange}
          />
        </div>
        <form onSubmit={handleSubmit} className="panel-socio-form">
          <div className="form-columns">
            <div className="form-column">
              <label>Nombre Completo</label>
              <input name="nombreCompleto" value={form.nombreCompleto} onChange={handleChange} required />
              <label>Documento</label>
              <input name="documento" value={form.documento || ''} onChange={handleChange} required />
              <label>Tipo de Documento</label>
              <select name="tipoDocumento" value={form.tipoDocumento || ''} onChange={handleChange} required>
                <option value="">Seleccionar</option>
                <option value="CI Uruguay">CI Uruguay</option>
                <option value="Otro">Otro</option>
              </select>
              <label>Administrador</label>
              <select name="administrador" value={form.administrador} onChange={handleChange} required>
                <option value="Sí">Sí</option>
                <option value="No">No</option>
              </select>
            </div>
            <div className="form-column">
              <label>Dirección</label>
              <input name="direccion" value={form.direccion} onChange={handleChange} />
              <label>Correo electrónico</label>
              <input name="correo" type="email" value={form.correo} onChange={handleChange} />
              <label>Teléfono</label>
              <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center', width: '100%', justifyContent: 'center'}}>
                <select 
                  value={codigoPais} 
                  onChange={handleCodigoPaisChange}
                  style={{
                    padding: '0.4rem 0.7rem',
                    border: '1px solid #D32F2F',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    minWidth: '140px',
                    maxWidth: '140px',
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                  }}
                >
                  <option value="+598">🇺🇾 Uruguay (+598)</option>
                  <option value="+93">🇦🇫 Afganistán (+93)</option>
                  <option value="+27">🇿🇦 Sudáfrica (+27)</option>
                  <option value="+49">🇩🇪 Alemania (+49)</option>
                  <option value="+213">🇩🇿 Argelia (+213)</option>
                  <option value="+966">🇸🇦 Arabia Saudita (+966)</option>
                  <option value="+54">🇦🇷 Argentina (+54)</option>
                  <option value="+43">🇦🇹 Austria (+43)</option>
                  <option value="+61">🇦🇺 Australia (+61)</option>
                  <option value="+880">🇧🇩 Bangladesh (+880)</option>
                  <option value="+32">🇧🇪 Bélgica (+32)</option>
                  <option value="+591">🇧🇴 Bolivia (+591)</option>
                  <option value="+975">🇧🇹 Bután (+975)</option>
                  <option value="+267">🇧🇼 Botsuana (+267)</option>
                  <option value="+55">🇧🇷 Brasil (+55)</option>
                  <option value="+257">🇧🇮 Burundi (+257)</option>
                  <option value="+855">🇰🇭 Camboya (+855)</option>
                  <option value="+1">🇨🇦 Canadá (+1)</option>
                  <option value="+682">🇨🇰 Islas Cook (+682)</option>
                  <option value="+56">🇨🇱 Chile (+56)</option>
                  <option value="+86">🇨🇳 China (+86)</option>
                  <option value="+57">🇨🇴 Colombia (+57)</option>
                  <option value="+269">🇰🇲 Comoras (+269)</option>
                  <option value="+506">🇨🇷 Costa Rica (+506)</option>
                  <option value="+53">🇨🇺 Cuba (+53)</option>
                  <option value="+850">🇰🇵 Corea del Norte (+850)</option>
                  <option value="+82">🇰🇷 Corea del Sur (+82)</option>
                  <option value="+235">🇹🇩 Chad (+235)</option>
                  <option value="+45">🇩🇰 Dinamarca (+45)</option>
                  <option value="+971">🇦🇪 Emiratos Árabes Unidos (+971)</option>
                  <option value="+593">🇪🇨 Ecuador (+593)</option>
                  <option value="+20">🇪🇬 Egipto (+20)</option>
                  <option value="+34">🇪🇸 España (+34)</option>
                  <option value="+1">🇺🇸 Estados Unidos (+1)</option>
                  <option value="+291">🇪🇷 Eritrea (+291)</option>
                  <option value="+251">🇪🇹 Etiopía (+251)</option>
                  <option value="+358">🇫🇮 Finlandia (+358)</option>
                  <option value="+679">🇫🇯 Fiyi (+679)</option>
                  <option value="+33">🇫🇷 Francia (+33)</option>
                  <option value="+240">🇬🇶 Guinea Ecuatorial (+240)</option>
                  <option value="+233">🇬🇭 Ghana (+233)</option>
                  <option value="+502">🇬🇹 Guatemala (+502)</option>
                  <option value="+245">🇬🇼 Guinea-Bisáu (+245)</option>
                  <option value="+504">🇭🇳 Honduras (+504)</option>
                  <option value="+852">🇭🇰 Hong Kong (+852)</option>
                  <option value="+36">🇭🇺 Hungría (+36)</option>
                  <option value="+91">🇮🇳 India (+91)</option>
                  <option value="+62">🇮🇩 Indonesia (+62)</option>
                  <option value="+353">🇮🇪 Irlanda (+353)</option>
                  <option value="+972">🇮🇱 Israel (+972)</option>
                  <option value="+39">🇮🇹 Italia (+39)</option>
                  <option value="+81">🇯🇵 Japón (+81)</option>
                  <option value="+253">🇩🇯 Yibuti (+253)</option>
                  <option value="+686">🇰🇮 Kiribati (+686)</option>
                  <option value="+254">🇰🇪 Kenia (+254)</option>
                  <option value="+856">🇱🇦 Laos (+856)</option>
                  <option value="+266">🇱🇸 Lesoto (+266)</option>
                  <option value="+94">🇱🇰 Sri Lanka (+94)</option>
                  <option value="+60">🇲🇾 Malasia (+60)</option>
                  <option value="+960">🇲🇻 Maldivas (+960)</option>
                  <option value="+261">🇲🇬 Madagascar (+261)</option>
                  <option value="+692">🇲🇭 Islas Marshall (+692)</option>
                  <option value="+52">🇲🇽 México (+52)</option>
                  <option value="+691">🇫🇲 Micronesia (+691)</option>
                  <option value="+95">🇲🇲 Myanmar (+95)</option>
                  <option value="+230">🇲🇺 Mauricio (+230)</option>
                  <option value="+505">🇳🇮 Nicaragua (+505)</option>
                  <option value="+234">🇳🇬 Nigeria (+234)</option>
                  <option value="+683">🇳🇺 Niue (+683)</option>
                  <option value="+64">🇳🇿 Nueva Zelanda (+64)</option>
                  <option value="+687">🇳🇨 Nueva Caledonia (+687)</option>
                  <option value="+47">🇳🇴 Noruega (+47)</option>
                  <option value="+977">🇳🇵 Nepal (+977)</option>
                  <option value="+31">🇳🇱 Países Bajos (+31)</option>
                  <option value="+507">🇵🇦 Panamá (+507)</option>
                  <option value="+675">🇵🇬 Papúa Nueva Guinea (+675)</option>
                  <option value="+595">🇵🇾 Paraguay (+595)</option>
                  <option value="+51">🇵🇪 Perú (+51)</option>
                  <option value="+63">🇵🇭 Filipinas (+63)</option>
                  <option value="+48">🇵🇱 Polonia (+48)</option>
                  <option value="+689">🇵🇫 Polinesia Francesa (+689)</option>
                  <option value="+351">🇵🇹 Portugal (+351)</option>
                  <option value="+44">🇬🇧 Reino Unido (+44)</option>
                  <option value="+420">🇨🇿 República Checa (+420)</option>
                  <option value="+242">🇨🇬 República del Congo (+242)</option>
                  <option value="+243">🇨🇩 República Democrática del Congo (+243)</option>
                  <option value="+236">🇨🇫 República Centroafricana (+236)</option>
                  <option value="+7">🇷🇺 Rusia (+7)</option>
                  <option value="+250">🇷🇼 Ruanda (+250)</option>
                  <option value="+685">🇼🇸 Samoa (+685)</option>
                  <option value="+239">🇸🇹 Santo Tomé y Príncipe (+239)</option>
                  <option value="+221">🇸🇳 Senegal (+221)</option>
                  <option value="+248">🇸🇨 Seychelles (+248)</option>
                  <option value="+65">🇸🇬 Singapur (+65)</option>
                  <option value="+252">🇸🇴 Somalia (+252)</option>
                  <option value="+503">🇸🇻 El Salvador (+503)</option>
                  <option value="+46">🇸🇪 Suecia (+46)</option>
                  <option value="+41">🇨🇭 Suiza (+41)</option>
                  <option value="+268">🇸🇿 Suazilandia (+268)</option>
                  <option value="+66">🇹🇭 Tailandia (+66)</option>
                  <option value="+886">🇹🇼 Taiwán (+886)</option>
                  <option value="+255">🇹🇿 Tanzania (+255)</option>
                  <option value="+690">🇹🇰 Tokelau (+690)</option>
                  <option value="+216">🇹🇳 Túnez (+216)</option>
                  <option value="+90">🇹🇷 Turquía (+90)</option>
                  <option value="+688">🇹🇻 Tuvalu (+688)</option>
                  <option value="+380">🇺🇦 Ucrania (+380)</option>
                  <option value="+256">🇺🇬 Uganda (+256)</option>
                  <option value="+58">🇻🇪 Venezuela (+58)</option>
                  <option value="+84">🇻🇳 Vietnam (+84)</option>
                  <option value="+681">🇼🇫 Wallis y Futuna (+681)</option>
                  <option value="+967">🇾🇪 Yemen (+967)</option>
                  <option value="+260">🇿🇲 Zambia (+260)</option>
                  <option value="+263">🇿🇼 Zimbabue (+263)</option>
                </select>
                <input 
                  name="telefono" 
                  value={form.telefono} 
                  onChange={handleTelefonoChange}
                  placeholder="Número de teléfono"
                  style={{
                    flex: 1,
                    minWidth: '160px',
                    maxWidth: '160px'
                  }}
                />
              </div>
              <label>Fecha de nacimiento</label>
              <input name="fechaNacimiento" type="date" value={form.fechaNacimiento} onChange={handleChange} />
            </div>
          </div>
          {mensaje && <div className="panel-socio-mensaje">{mensaje}</div>}
          {error && <div className="panel-socio-error">{error}</div>}
        </form>
        <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', cursor: 'pointer'}}>
          <input 
            type="checkbox" 
            name="novedadesMail" 
            checked={form.novedadesMail || false} 
            onChange={e => setForm({ ...form, novedadesMail: e.target.checked })} 
            style={{width: 'auto', margin: 0, cursor: 'pointer'}}
          />
          <span>Deseo que me notifiquen de las novedades del club por mail</span>
        </label>
        <div className="panel-socio-botones">
          <button type="button" className="btn-panel-socio btn-accion" onClick={handleSubmit} disabled={loading} style={{marginBottom: 8}}>
            {loading ? 'Guardando...' : '💾 Guardar cambios'}
          </button>
          <button type="button" className="btn-panel-socio btn-accion" onClick={handleChangePassword}>
            🔐 Cambiar contraseña
          </button>
        </div>
        <button className="btn-panel-socio btn-accion" onClick={() => navigate('/admin')} style={{marginTop: 16, marginBottom: 8}}>
          ⬅️ Volver al Panel
        </button>
        <button className="btn-panel-socio btn-cerrar-sesion" onClick={handleLogout} style={{marginTop: 8}}>
          🚪 Cerrar sesión
        </button>
      </div>
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content logout-modal">
            <div className="logout-modal-header">
              <h4 style={{margin: 0, color: 'white', textAlign: 'center'}}>⚠️ Cambios sin guardar</h4>
            </div>
            <div className="logout-modal-body">
              <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
                <p style={{margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#333', lineHeight: '1.5'}}>
                  Tienes cambios sin guardar en tu perfil.
                </p>
                <p style={{margin: 0, fontSize: '1rem', color: '#666', fontWeight: '500'}}>
                  ¿Qué deseas hacer?
                </p>
              </div>
              <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                <button 
                  className="btn-panel-socio" 
                  onClick={handleSaveAndLogout}
                  style={{
                    padding: '0.8rem',
                    fontSize: '1rem',
                    background: 'linear-gradient(90deg, #4CAF50 0%, #45a049 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  💾 Guardar y salir
                </button>
                <button 
                  className="btn-panel-socio" 
                  onClick={handleLogoutWithoutSave}
                  style={{
                    padding: '0.8rem',
                    fontSize: '1rem',
                    background: 'linear-gradient(90deg, #FF9800 0%, #F57C00 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  ❌ Salir sin guardar
                </button>
                <button 
                  className="btn-panel-socio" 
                  onClick={handleCancelModal}
                  style={{
                    padding: '0.8rem',
                    fontSize: '1rem',
                    background: 'linear-gradient(90deg, #BDBDBD 0%, #757575 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content password-modal">
            <div className="password-modal-header">
              <h4 style={{margin: 0, color: 'white', textAlign: 'center'}}>🔐 Cambiar contraseña</h4>
            </div>
            <div className="password-modal-body">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Nueva contraseña"
                style={{marginBottom: 12, width: '100%'}}
              />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirmar contraseña"
                style={{marginBottom: 12, width: '100%'}}
              />
              <div style={{display: 'flex', gap: 8, marginBottom: 12}}>
                <label style={{fontSize: '0.9rem'}}>
                  <input type="checkbox" checked={showPassword} onChange={e => setShowPassword(e.target.checked)} /> Mostrar contraseña
                </label>
                <label style={{fontSize: '0.9rem'}}>
                  <input type="checkbox" checked={showConfirmPassword} onChange={e => setShowConfirmPassword(e.target.checked)} /> Mostrar confirmación
                </label>
              </div>
              <div style={{display: 'flex', gap: 12, marginTop: 'auto', justifyContent: 'center'}}>
                <button className="btn-panel-socio" onClick={handleSavePassword} style={{background: 'linear-gradient(90deg, #4CAF50 0%, #45a049 100%)', color: 'white', flex: 1}}>
                  Guardar contraseña
                </button>
                <button className="btn-panel-socio" onClick={handleCancelPassword} style={{background: 'linear-gradient(90deg, #BDBDBD 0%, #757575 100%)', color: 'white', flex: 1}}>
                  Cancelar
                </button>
              </div>
              {error && <div className="panel-socio-error">{error}</div>}
              {mensaje && <div className="panel-socio-mensaje">{mensaje}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditarAdmin; 